import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Printer, Save, FileText, Loader2, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "./AdminOverview";

export default function AdminQuoteDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [quote, setQuote] = useState(null);
    const [status, setStatus] = useState("");
    const [notes, setNotes] = useState("");
    const [adjustedTotal, setAdjustedTotal] = useState("");
    const [saving, setSaving] = useState(false);
    const [resending, setResending] = useState(false);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/admin/quotes/${id}`);
                setQuote(data);
                setStatus(data.status);
                setNotes(data.admin_notes || "");
                setAdjustedTotal(data.adjusted_total ?? "");
            } catch (e) {
                toast.error(formatApiError(e));
            }
        })();
    }, [id]);

    const save = async () => {
        setSaving(true);
        try {
            const payload = { status, admin_notes: notes };
            if (adjustedTotal !== "") payload.adjusted_total = Number(adjustedTotal);
            const { data } = await api.patch(`/admin/quotes/${id}`, payload);
            setQuote(data);
            toast.success("Guardado!");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    const printPdf = () => window.print();

    const resendEmail = async () => {
        setResending(true);
        try {
            await api.post(`/admin/quotes/${id}/resend-email`);
            toast.success("Email reenviado ao cliente");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setResending(false);
        }
    };

    const deleteQuote = async () => {
        if (!window.confirm(`Eliminar definitivamente o pedido ${quote.reference}? Esta ação não pode ser revertida.`)) return;
        setDeleting(true);
        try {
            await api.delete(`/admin/quotes/${id}`);
            toast.success("Pedido eliminado");
            navigate("/admin/pedidos");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setDeleting(false);
        }
    };

    if (!quote) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin" /></div>;
    const calc = quote.calculation || {};
    const c = quote.customer || {};
    const loc = quote.location || {};

    return (
        <div className="space-y-6" data-testid="quote-detail">
            <div className="no-print flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="sm" onClick={() => navigate("/admin/pedidos")} data-testid="back-to-list">
                        <ArrowLeft className="w-4 h-4 mr-1" /> Voltar
                    </Button>
                    <div>
                        <div className="font-mono text-sm text-white/50">{quote.reference}</div>
                        <h1 className="font-heading text-2xl font-bold">{c.name}</h1>
                    </div>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={resendEmail} disabled={resending} data-testid="resend-email">
                        {resending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Reenviar email ao cliente
                    </Button>
                    <Button variant="outline" onClick={printPdf} data-testid="print-pdf"><Printer className="w-4 h-4 mr-2" /> Imprimir / PDF</Button>
                </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    <div className="ab-card p-8">
                        <div className="flex items-center gap-3 justify-between mb-6">
                            <div>
                                <div className="text-xs text-white/50 uppercase tracking-widest">Orçamento</div>
                                <div className="font-mono font-bold text-lg">{quote.reference}</div>
                            </div>
                            <StatusBadge status={quote.status} />
                        </div>
                        <div className="grid sm:grid-cols-2 gap-6 pb-6 border-b border-[#2A2A2A]">
                            <InfoBlock title="Cliente" items={[
                                ["Nome", c.name],
                                ["Telefone", c.phone],
                                ["Email", c.email],
                            ]} />
                            <InfoBlock title="Localização" items={[
                                ["Código postal", loc.postal_code],
                                ["Localidade", loc.locality],
                                ["Distrito", loc.district],
                                ["Andar / Elevador", `${loc.floor || "-"} / ${loc.elevator || "-"}`],
                                ["Estacionamento", loc.parking],
                                ["Morada", loc.address || "-"],
                            ]} />
                        </div>
                        <div className="pt-6">
                            <h3 className="font-heading font-bold mb-3">Itens</h3>
                            <div className="space-y-2">
                                {(calc.breakdown || []).map((b, i) => (
                                    <div key={i} className="flex justify-between py-2 border-b border-[#2A2A2A]">
                                        <span>{b.label}</span>
                                        <span className="font-heading font-semibold">{b.price?.toFixed(2)} €</span>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-4 space-y-1 text-sm">
                                <Row label="Subtotal" v={calc.subtotal} />
                                {calc.supplements > 0 && <Row label="Suplementos" v={calc.supplements} />}
                                {calc.travel_fee > 0 && <Row label="Deslocação" v={calc.travel_fee} />}
                                {calc.discount > 0 && <Row label={`Desconto ${calc.promo?.code ? `(${calc.promo.code})` : ""}`} v={-calc.discount} />}
                            </div>
                            <div className="mt-4 pt-4 border-t border-[#2A2A2A] flex items-baseline justify-between">
                                <div className="font-heading font-bold">Total estimado</div>
                                <div className="font-heading font-black text-3xl text-[#C9A961]">{calc.total?.toFixed(2)} €</div>
                            </div>
                            {quote.adjusted_total != null && (
                                <div className="mt-2 pt-2 border-t border-dashed border-[#2A2A2A] flex items-baseline justify-between">
                                    <div className="font-heading font-bold text-white/70">Total ajustado</div>
                                    <div className="font-heading font-black text-3xl text-emerald-400">{Number(quote.adjusted_total).toFixed(2)} €</div>
                                </div>
                            )}
                        </div>
                        {(quote.conditions?.length > 0 || quote.disinfection || quote.impermeabilization) && (
                            <div className="mt-6 pt-6 border-t border-[#2A2A2A]">
                                <h3 className="font-heading font-bold mb-3">Estado / Necessidades</h3>
                                <div className="flex flex-wrap gap-2">
                                    {quote.conditions?.map((c) => <span key={c} className="text-xs px-2 py-1 bg-[#1F1F1F] text-white/70 rounded-full">{c.replace(/_/g, " ")}</span>)}
                                    {quote.disinfection && <span className="text-xs px-2 py-1 bg-[#C9A961]/10 text-[#C9A961] rounded-full font-medium">Desinfeção</span>}
                                    {quote.impermeabilization && <span className="text-xs px-2 py-1 bg-[#C9A961]/10 text-[#C9A961] rounded-full font-medium">Impermeabilização · sob consulta</span>}
                                </div>
                            </div>
                        )}
                        {quote.notes && (
                            <div className="mt-6 pt-6 border-t border-[#2A2A2A]">
                                <h3 className="font-heading font-bold mb-2">Observações do cliente</h3>
                                <p className="text-white/60">{quote.notes}</p>
                            </div>
                        )}
                        {quote.photos?.length > 0 && (
                            <div className="mt-6 pt-6 border-t border-[#2A2A2A]">
                                <h3 className="font-heading font-bold mb-3">Fotografias</h3>
                                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                                    {quote.photos.map((p, i) => (
                                        <a key={i} href={fileUrl(p)} target="_blank" rel="noreferrer" className="aspect-square rounded-lg overflow-hidden border border-[#2A2A2A] block">
                                            <img src={fileUrl(p)} alt={`Foto ${i + 1}`} className="w-full h-full object-cover" />
                                        </a>
                                    ))}
                                </div>
                            </div>
                        )}
                        <div className="mt-6 pt-6 border-t border-[#2A2A2A] text-xs text-white/50">
                            Criado em {new Date(quote.created_at).toLocaleString("pt-PT")} · Data preferida: {quote.preferred_date || "-"} · Período: {quote.preferred_period}
                        </div>
                    </div>
                </div>

                <div className="space-y-4 no-print">
                    <div className="ab-card p-6">
                        <h3 className="font-heading font-bold mb-4 flex items-center gap-2"><FileText className="w-4 h-4" /> Gestão do pedido</h3>
                        <div className="space-y-4">
                            <div>
                                <Label>Estado</Label>
                                <Select value={status} onValueChange={setStatus}>
                                    <SelectTrigger data-testid="update-status"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="novo">Novo</SelectItem>
                                        <SelectItem value="em_analise">Em análise</SelectItem>
                                        <SelectItem value="orcamento_enviado">Orçamento enviado</SelectItem>
                                        <SelectItem value="aceite">Aceite</SelectItem>
                                        <SelectItem value="agendado">Agendado</SelectItem>
                                        <SelectItem value="concluido">Concluído</SelectItem>
                                        <SelectItem value="cancelado">Cancelado</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Total ajustado (€)</Label>
                                <Input type="number" step="0.01" value={adjustedTotal} onChange={(e) => setAdjustedTotal(e.target.value)} placeholder={calc.total?.toFixed(2)} data-testid="update-total" />
                            </div>
                            <div>
                                <Label>Notas internas</Label>
                                <Textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="update-notes" />
                            </div>
                            <Button onClick={save} disabled={saving} className="ab-btn-primary w-full rounded-none uppercase tracking-widest" data-testid="save-quote">
                                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Guardar
                            </Button>
                            <div className="pt-4 mt-4 border-t border-[#2A2A2A]">
                                <Button onClick={deleteQuote} disabled={deleting} variant="ghost" className="w-full text-red-400 hover:bg-red-500/10 uppercase tracking-widest text-xs" data-testid="delete-quote">
                                    {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />} Eliminar pedido
                                </Button>
                                <p className="text-[11px] text-white/40 text-center mt-2">Recomendado para pedidos cancelados sem intenção de recuperar.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function InfoBlock({ title, items }) {
    return (
        <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-white/40 mb-2">{title}</div>
            <div className="space-y-1 text-sm">
                {items.map(([k, v]) => (
                    <div key={k}><span className="text-white/50">{k}:</span> <span className="font-medium">{v || "-"}</span></div>
                ))}
            </div>
        </div>
    );
}

function Row({ label, v }) {
    return <div className="flex justify-between"><span className="text-white/60">{label}</span><span>{Number(v).toFixed(2)} €</span></div>;
}
